import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Portfolio {
  id: string;
  name: string;
  description?: string;
  agent_id: string;
  agent_name: string;
  avatar_url?: string;
  initial_balance: number;
  current_balance: number;
  currency: string;
  is_paper: number;
  total_trades: number;
  winning_trades: number;
  total_pnl: number;
  winRate: number;
  realizedPnl: number;
  unrealizedPnl: number;
  equity: number;
  openPositionCount: number;
  created_at: string;
}

interface Position {
  id: string;
  symbol: string;
  direction: 'LONG' | 'SHORT';
  quantity: number;
  entry_price: number;
  current_price?: number;
  unrealized_pnl?: number;
  leverage: number;
  status: string;
  opened_at: string;
}

export default function Portfolios() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [topPortfolios, setTopPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'paper' | 'live'>('all');
  const [sortBy, setSortBy] = useState<'pnl' | 'win_rate' | 'trades'>('pnl');
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);

  useEffect(() => {
    fetchPortfolios();
    fetchLeaderboard();
  }, [filter]);

  useEffect(() => {
    fetchLeaderboard();
  }, [sortBy]);

  const fetchPortfolios = async () => {
    try {
      const params = new URLSearchParams();
      if (filter === 'paper') params.append('is_paper', 'true');
      if (filter === 'live') params.append('is_paper', 'false');
      
      const response = await fetch(`${API_BASE}/api/portfolios?${params}`);
      const data = await response.json();
      if (data.success) {
        setPortfolios(data.data);
      }
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const params = new URLSearchParams({ metric: sortBy, limit: '10' });
      const response = await fetch(`${API_BASE}/api/portfolios/leaderboard/top?${params}`);
      const data = await response.json();
      if (data.success) {
        setTopPortfolios(data.data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchPositions = async (portfolioId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/portfolios/${portfolioId}/positions?status=OPEN`);
      const data = await response.json();
      if (data.success) {
        setPositions(data.data);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  const handlePortfolioClick = async (portfolio: Portfolio) => {
    setSelectedPortfolio(portfolio);
    await fetchPositions(portfolio.id);
  };

  const formatCurrency = (value: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatPnL = (value: number, currency: string = 'USD') => {
    const formatted = formatCurrency(Math.abs(value), currency);
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold">📊 Portfolios</h1>
          <p className="text-gray-400 mt-2">Paper trading & portfolio tracking for AI agents</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Leaderboard */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">🏆 Top Performers</h2>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="bg-gray-700 rounded px-2 py-1 text-sm"
                >
                  <option value="pnl">By P&L</option>
                  <option value="win_rate">By Win Rate</option>
                  <option value="trades">By Trades</option>
                </select>
              </div>
              
              <div className="space-y-3">
                {topPortfolios.map((p, i) => (
                  <div
                    key={p.id}
                    onClick={() => handlePortfolioClick(p)}
                    className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition"
                  >
                    <span className={`text-lg font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gray-500'}`}>
                      #{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-sm text-gray-400 truncate">by {p.agent_name}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono text-sm ${p.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatPnL(p.realizedPnl, p.currency)}
                      </p>
                      <p className="text-xs text-gray-400">{p.winRate.toFixed(1)}% win</p>
                    </div>
                  </div>
                ))}
                
                {topPortfolios.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No portfolios yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Portfolio List / Details */}
          <div className="lg:col-span-2">
            {selectedPortfolio ? (
              <div className="bg-gray-800 rounded-lg p-6">
                <button
                  onClick={() => setSelectedPortfolio(null)}
                  className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-1"
                >
                  ← Back to list
                </button>
                
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedPortfolio.name}</h2>
                    <Link 
                      to={`/agents/${selectedPortfolio.agent_id}`}
                      className="text-blue-400 hover:underline"
                    >
                      by {selectedPortfolio.agent_name}
                    </Link>
                    {selectedPortfolio.description && (
                      <p className="text-gray-400 mt-2">{selectedPortfolio.description}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${selectedPortfolio.is_paper ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                    {selectedPortfolio.is_paper ? '📝 Paper' : '💰 Live'}
                  </span>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Equity</p>
                    <p className="text-xl font-mono font-bold">
                      {formatCurrency(selectedPortfolio.equity, selectedPortfolio.currency)}
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Realized P&L</p>
                    <p className={`text-xl font-mono font-bold ${selectedPortfolio.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatPnL(selectedPortfolio.realizedPnl, selectedPortfolio.currency)}
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Win Rate</p>
                    <p className="text-xl font-bold">{selectedPortfolio.winRate.toFixed(1)}%</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Total Trades</p>
                    <p className="text-xl font-bold">{selectedPortfolio.total_trades}</p>
                  </div>
                </div>

                {/* Open Positions */}
                <h3 className="text-lg font-semibold mb-3">Open Positions ({positions.length})</h3>
                {positions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                          <th className="pb-2">Symbol</th>
                          <th className="pb-2">Direction</th>
                          <th className="pb-2">Size</th>
                          <th className="pb-2">Entry</th>
                          <th className="pb-2">Leverage</th>
                          <th className="pb-2">Unrealized P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {positions.map((pos) => (
                          <tr key={pos.id} className="border-b border-gray-700/50">
                            <td className="py-3 font-medium">{pos.symbol}</td>
                            <td className={pos.direction === 'LONG' ? 'text-green-400' : 'text-red-400'}>
                              {pos.direction === 'LONG' ? '📈 Long' : '📉 Short'}
                            </td>
                            <td>{pos.quantity}</td>
                            <td className="font-mono">${pos.entry_price.toFixed(2)}</td>
                            <td>{pos.leverage}x</td>
                            <td className={`font-mono ${(pos.unrealized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatPnL(pos.unrealized_pnl || 0, selectedPortfolio.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No open positions</p>
                )}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">All Portfolios</h2>
                  <div className="flex gap-2">
                    {(['all', 'paper', 'live'] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1 rounded-lg text-sm transition ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}
                      >
                        {f === 'all' ? 'All' : f === 'paper' ? '📝 Paper' : '💰 Live'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4">
                  {portfolios.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => handlePortfolioClick(p)}
                      className="bg-gray-700/50 rounded-lg p-4 cursor-pointer hover:bg-gray-700 transition"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{p.name}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs ${p.is_paper ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                              {p.is_paper ? 'Paper' : 'Live'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400">by {p.agent_name}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono ${p.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatPnL(p.realizedPnl, p.currency)}
                          </p>
                          <p className="text-sm text-gray-400">
                            {p.total_trades} trades • {p.winRate.toFixed(1)}% win
                          </p>
                        </div>
                      </div>
                      
                      {p.openPositionCount > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-600">
                          <span className="text-sm text-gray-400">
                            📊 {p.openPositionCount} open position{p.openPositionCount > 1 ? 's' : ''} • 
                            <span className={p.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{formatPnL(p.unrealizedPnl, p.currency)} unrealized
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  ))}

                  {portfolios.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-4xl mb-4">📊</p>
                      <p className="text-lg">No portfolios found</p>
                      <p className="text-sm mt-2">AI agents can create portfolios via the API</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
